import { useEffect, useMemo, useState, type CSSProperties, type DragEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, Field } from "@booking-seconds/ui";
import {
  AlertTriangle,
  CalendarDays,
  ClipboardList,
  Columns3,
  ListChecks,
  MapPinned,
  Rows3,
  RefreshCw,
  UserCheck,
  UserX,
  WandSparkles
} from "lucide-react";
import { Link } from "react-router-dom";
import type {
  SchedulerDayBoard,
  SchedulerDayBookingAssignmentUpdateInput,
  SchedulerDayTeam,
  SchedulerDayTeamInput,
  SchedulerHomeTeamInput
} from "@booking-seconds/types";
import { DateInput } from "./date-input";
import { apiFetch } from "./lib/api";
import { OpsHeader, OpsMetricCard, OpsMetricGrid, OpsPageShell, OpsPanel } from "./pages/admin/ops-ui";

type LocationItem = {
  id: string;
  name?: string;
  active?: number | boolean;
  timezone?: string;
  businessHours?: Array<{
    day?: string;
    start?: string;
    end?: string;
    enabled?: boolean;
  }>;
};

type StaffDirectoryCategory = "maids" | "drivers" | "office";

type StaffSettingsItem = {
  id: string;
  code?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  active?: number | boolean;
  workerGroup?: string;
  staffTypeId?: string;
  assignedLocationIds?: string[];
  assignedLocationIdsJson?: string[] | string;
};

type StaffTypeSettingsItem = {
  id: string;
  directoryCategory?: StaffDirectoryCategory | "all";
};

type HomeTeamDraft = {
  id: string;
  name: string;
  defaultDriverStaffId: string;
  leadStaffId: string;
  memberStaffIds: string[];
};

type DayTeamDraft = {
  id: string;
  name: string;
  driverStaffId: string;
  memberStaffIds: string[];
  targetTeamSize: string;
  teamState: "ready" | "needs_review";
  routeStartTime: string;
  notes: string;
};

type DispatcherView = "horizontal" | "vertical" | "map" | "list";
type SchedulerBooking = SchedulerDayBoard["bookings"][number];
type DispatcherRoutePlan = {
  booking: SchedulerBooking;
  routeIndex: number;
  requestedStartMinutes: number | null;
  plannedStartMinutes: number;
  plannedEndMinutes: number;
  startsAfterBookedTime: boolean;
  endsAfterWorkday: boolean;
};
type DispatcherRouteWarning = {
  key: string;
  teamName: string;
  message: string;
  tone: "warning" | "danger";
};

function getTodayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function isActiveFlag(value: unknown) {
  return value !== false && Number(value ?? 1) !== 0;
}

function normalizeStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return Array.from(new Set(value.map((entry) => String(entry ?? "").trim()).filter(Boolean)));
  }

  const text = String(value ?? "").trim();
  if (!text) {
    return [];
  }

  if (text.startsWith("[")) {
    try {
      return normalizeStringList(JSON.parse(text));
    } catch {
      return [];
    }
  }

  return Array.from(
    new Set(
      text
        .split(/[,\n|]/)
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );
}

function parseStaffConfig(value: unknown) {
  if (!value || typeof value !== "string") {
    return {} as Record<string, unknown>;
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function getStaffCode(
  staff: Pick<StaffSettingsItem, "code" | "id"> &
    Record<string, unknown>
) {
  const config = parseStaffConfig(staff.configJson ?? staff.config_json);
  const candidates = [
    staff.code,
    staff.workerCode,
    staff.worker_code,
    config.code,
    config.workerCode,
    config.worker_code
  ];

  for (const candidate of candidates) {
    const code = String(candidate ?? "").trim();
    if (code) {
      return code;
    }
  }

  return "";
}

function getStaffName(staff: Pick<StaffSettingsItem, "firstName" | "lastName" | "name" | "id"> & Record<string, unknown>) {
  const firstName = String(staff.firstName ?? staff.first_name ?? "").trim();
  const lastName = String(staff.lastName ?? staff.last_name ?? "").trim();
  const combined = `${firstName} ${lastName}`.trim();
  return combined || String(staff.name ?? "").trim() || String(staff.id ?? "");
}

function getMaidLabel(staff: Pick<StaffSettingsItem, "code" | "firstName" | "lastName" | "name" | "id"> & Record<string, unknown>) {
  const code = getStaffCode(staff);
  return code || getStaffName(staff);
}

function resolveDirectoryCategory(staff: StaffSettingsItem, staffTypeById: Map<string, StaffDirectoryCategory>) {
  const staffTypeId = String(staff.staffTypeId ?? "").trim();
  if (staffTypeId && staffTypeById.has(staffTypeId)) {
    return staffTypeById.get(staffTypeId)!;
  }

  const workerGroup = String(staff.workerGroup ?? "").trim().toLowerCase();
  if (workerGroup === "driver" || workerGroup === "drivers") {
    return "drivers";
  }
  if (workerGroup === "office" || workerGroup === "office staff") {
    return "office";
  }
  return "maids";
}

function isLocationEligible(staff: StaffSettingsItem, locationId: string) {
  const assignedLocationIds = normalizeStringList(staff.assignedLocationIds ?? staff.assignedLocationIdsJson);
  return !assignedLocationIds.length || assignedLocationIds.includes(locationId);
}

function toggleSelection(selected: string[], value: string) {
  return selected.includes(value) ? selected.filter((entry) => entry !== value) : [...selected, value];
}

function createEmptyHomeTeamDraft(): HomeTeamDraft {
  return {
    id: "",
    name: "",
    defaultDriverStaffId: "",
    leadStaffId: "",
    memberStaffIds: []
  };
}

function createEmptyDayTeamDraft(): DayTeamDraft {
  return {
    id: "",
    name: "",
    driverStaffId: "",
    memberStaffIds: [],
    targetTeamSize: "2",
    teamState: "needs_review",
    routeStartTime: "",
    notes: ""
  };
}

function deriveManualOverrides(board?: SchedulerDayBoard) {
  const overrides = new Map<string, "available" | "off">();
  for (const staff of board?.availableMaids ?? []) {
    if (staff.source === "manual_override") {
      overrides.set(staff.staffId, "available");
    }
  }
  for (const staff of board?.availableDrivers ?? []) {
    if (staff.source === "manual_override") {
      overrides.set(staff.staffId, "available");
    }
  }
  for (const staff of board?.offStaff ?? []) {
    if (staff.reason === "manual_off") {
      overrides.set(staff.staffId, "off");
    }
  }
  return overrides;
}

function buildToneForWarning(warning: string) {
  return /unassigned|needs review/i.test(warning) ? "warning" : "info";
}

function buildToneForOffReason(reason: string) {
  if (reason === "time_off") {
    return "danger";
  }
  if (reason === "manual_off") {
    return "warning";
  }
  return "neutral";
}

function parseTimeToMinutes(value: string | null | undefined) {
  const match = String(value ?? "").trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }

  return Math.max(0, Math.min(24 * 60, hours * 60 + minutes));
}

function formatMinutesAsClock(totalMinutes: number) {
  const minutes = Math.max(0, Math.round(totalMinutes));
  const hours24 = Math.floor(minutes / 60) % 24;
  const minutePart = minutes % 60;
  const suffix = hours24 >= 12 ? "PM" : "AM";
  const displayHours = hours24 % 12 || 12;
  return `${displayHours}:${String(minutePart).padStart(2, "0")} ${suffix}`;
}

function formatDurationMinutes(totalMinutes: number) {
  const safeMinutes = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  if (!hours) {
    return `${minutes} min`;
  }
  if (!minutes) {
    return `${hours} hr${hours === 1 ? "" : "s"}`;
  }
  return `${hours} hr ${minutes} min`;
}

function getWeekdayKey(date: string, timeZone = "America/Chicago") {
  const parts = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone }).formatToParts(new Date(`${date}T12:00:00`));
  return String(parts.find((part) => part.type === "weekday")?.value ?? "").slice(0, 3).toLowerCase();
}

function getTimeZoneMinutes(value: string, timeZone = "America/Chicago") {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);
  const hours = Number(parts.find((part) => part.type === "hour")?.value ?? "0");
  const minutes = Number(parts.find((part) => part.type === "minute")?.value ?? "0");
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
}

function getBookingDurationMinutes(booking: SchedulerBooking) {
  const start = new Date(booking.startsAt).getTime();
  const end = new Date(booking.endsAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return 30;
  }

  return Math.max(15, Math.round((end - start) / 60000));
}

function resolveDispatcherWorkday(location: LocationItem | undefined, selectedDate: string, bookings: SchedulerBooking[]) {
  const timeZone = String(location?.timezone ?? "").trim() || "America/Chicago";
  const weekday = getWeekdayKey(selectedDate, timeZone);
  const hours = (location?.businessHours ?? []).find((entry) => {
    const enabled = entry.enabled !== false;
    return enabled && String(entry.day ?? "").slice(0, 3).toLowerCase() === weekday;
  });
  const configuredStart = parseTimeToMinutes(hours?.start);
  const configuredEnd = parseTimeToMinutes(hours?.end);

  const bookingStarts = bookings
    .map((booking) => getTimeZoneMinutes(booking.startsAt, timeZone))
    .filter((entry): entry is number => entry != null);
  const bookingEnds = bookings
    .map((booking) => {
      const start = getTimeZoneMinutes(booking.startsAt, timeZone);
      return start == null ? null : start + getBookingDurationMinutes(booking);
    })
    .filter((entry): entry is number => entry != null);

  const startMinutes = Math.min(configuredStart ?? 7 * 60 + 30, ...(bookingStarts.length ? bookingStarts : [configuredStart ?? 7 * 60 + 30]));
  const endMinutes = Math.max(configuredEnd ?? 15 * 60 + 30, ...(bookingEnds.length ? bookingEnds : [configuredEnd ?? 15 * 60 + 30]));

  return {
    timeZone,
    startMinutes: Math.floor(startMinutes / 30) * 30,
    endMinutes: Math.ceil(endMinutes / 30) * 30
  };
}

function buildTimelineTicks(startMinutes: number, endMinutes: number) {
  const firstTick = Math.ceil(startMinutes / 60) * 60;
  const ticks: number[] = [];
  for (let minute = firstTick; minute <= endMinutes; minute += 60) {
    ticks.push(minute);
  }
  return ticks;
}

function sortRouteBookings(bookings: SchedulerBooking[]) {
  return bookings.slice().sort((left, right) => {
    const leftSequence = left.assignment.sequenceIndex;
    const rightSequence = right.assignment.sequenceIndex;
    if (leftSequence != null && rightSequence != null && leftSequence !== rightSequence) {
      return leftSequence - rightSequence;
    }
    if (leftSequence != null && rightSequence == null) {
      return -1;
    }
    if (leftSequence == null && rightSequence != null) {
      return 1;
    }
    return new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime() || left.publicReference.localeCompare(right.publicReference);
  });
}

function formatBookingTimeRange(booking: SchedulerDayBoard["bookings"][number]) {
  const start = new Date(booking.startsAt);
  const end = new Date(booking.endsAt);
  return `${start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} - ${end.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit"
  })}`;
}

function SchedulerBookingAssignmentCard(props: {
  booking: SchedulerDayBoard["bookings"][number];
  teams: SchedulerDayTeam[];
  isPending: boolean;
  onSave: (bookingId: string, payload: SchedulerDayBookingAssignmentUpdateInput) => void;
  draggable?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const { booking, teams, isPending, onSave, draggable, onDragStart, onDragEnd } = props;
  const [selectedTeamId, setSelectedTeamId] = useState(booking.assignment.scheduleDayTeamId ?? "");
  const [isLocked, setIsLocked] = useState(booking.assignment.isLocked);
  const [note, setNote] = useState(booking.assignment.manualNote);

  useEffect(() => {
    setSelectedTeamId(booking.assignment.scheduleDayTeamId ?? "");
    setIsLocked(booking.assignment.isLocked);
    setNote(booking.assignment.manualNote);
  }, [booking.assignment.isLocked, booking.assignment.manualNote, booking.assignment.scheduleDayTeamId, booking.bookingId]);

  return (
    <div
      className="booking-list-item scheduler-booking-card"
      draggable={draggable}
      onDragStart={(event) => {
        if (draggable) {
          event.dataTransfer.setData("text/plain", booking.bookingId);
          onDragStart?.();
        }
      }}
      onDragEnd={onDragEnd}
      style={{ cursor: draggable ? "grab" : "default" }}
    >
      <div className="booking-list-item__top">
        <div>
          <strong>{booking.publicReference}</strong>
          <p>{booking.customerName}</p>
        </div>
        <div style={{ textAlign: "right" }}>
          <Badge tone={booking.assignment.scheduleDayTeamId ? "info" : "warning"}>
            {booking.assignment.scheduleDayTeamId ? "Assigned" : "Unassigned"}
          </Badge>
          <p>{new Date(booking.startsAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</p>
        </div>
      </div>
      <div className="booking-list-item__meta">
        <span>{booking.serviceAddress || "No service address"}</span>
        <span>{booking.arrivalWindowLabel || "Exact arrival window"}</span>
      </div>
      {booking.assignment.unassignedReasonText ? (
        <p className="card-copy" style={{ marginBottom: "0.75rem" }}>
          {booking.assignment.unassignedReasonText}
        </p>
      ) : null}
      {booking.assignment.manualNote ? (
        <p className="card-copy" style={{ marginBottom: "0.75rem" }}>
          Note: {booking.assignment.manualNote}
        </p>
      ) : null}
      <div className="stack--tight" style={{ display: "grid", gap: "0.75rem" }}>
        <Field label="Assign to team">
          <select value={selectedTeamId} disabled={!teams.length} onChange={(event) => setSelectedTeamId(event.target.value)}>
            <option value="">{teams.length ? "Leave unassigned" : "Build day teams first"}</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </Field>
        <label style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
          <input type="checkbox" checked={isLocked} onChange={(event) => setIsLocked(event.target.checked)} />
          Lock this assignment
        </label>
        <Field label="Dispatcher note">
          <textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
        <div className="customer-directory-actions">
          <Button
            variant="secondary"
            disabled={isPending || !teams.length}
            onClick={() => onSave(booking.bookingId, { scheduleDayTeamId: selectedTeamId || null, isLocked, note })}
          >
            Save assignment
          </Button>
        </div>
      </div>
    </div>
  );
}

function DispatcherBookingOverviewCard(props: {
  booking: SchedulerDayBoard["bookings"][number];
  teams: SchedulerDayTeam[];
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const { booking, teams, onDragStart, onDragEnd } = props;
  const assignedTeam = booking.assignment.scheduleDayTeamId
    ? teams.find((team) => team.id === booking.assignment.scheduleDayTeamId)
    : null;

  return (
    <div
      className="booking-list-item scheduler-booking-card scheduler-booking-overview-card"
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData("text/plain", booking.bookingId);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      style={{ cursor: "grab" }}
    >
      <div className="booking-list-item__top">
        <div>
          <strong>{booking.publicReference}</strong>
          <p>{booking.customerName}</p>
        </div>
        <div style={{ textAlign: "right" }}>
          <Badge tone={assignedTeam ? "info" : "warning"}>{assignedTeam ? assignedTeam.name : "Unassigned"}</Badge>
          <p>{formatBookingTimeRange(booking)}</p>
        </div>
      </div>
      <div className="booking-list-item__meta">
        <span>{booking.serviceAddress || "No service address"}</span>
        <span>{booking.status}</span>
      </div>
    </div>
  );
}

function DispatcherViewButton({
  active,
  disabled = false,
  icon: Icon,
  label,
  onClick
}: {
  active: boolean;
  disabled?: boolean;
  icon: typeof Columns3;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className={`dispatcher-view-button ${active ? "is-active" : ""}`}
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? "Build day teams before switching dispatcher views." : undefined}
    >
      <Icon size={16} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

function DispatcherViewSwitcher({
  value,
  onChange,
  disabled = false
}: {
  value: DispatcherView;
  onChange: (value: DispatcherView) => void;
  disabled?: boolean;
}) {
  return (
    <div className={`dispatcher-view-switcher ${disabled ? "is-disabled" : ""}`} aria-label="Dispatcher view">
      <DispatcherViewButton
        active={value === "horizontal"}
        disabled={disabled}
        icon={Rows3}
        label="Horizontal"
        onClick={() => onChange("horizontal")}
      />
      <DispatcherViewButton
        active={value === "vertical"}
        disabled={disabled}
        icon={Columns3}
        label="Vertical"
        onClick={() => onChange("vertical")}
      />
      <DispatcherViewButton
        active={value === "map"}
        disabled={disabled}
        icon={MapPinned}
        label="Map"
        onClick={() => onChange("map")}
      />
      <DispatcherViewButton active={value === "list"} disabled={disabled} icon={ListChecks} label="List" onClick={() => onChange("list")} />
    </div>
  );
}

function DispatcherCompactBookingCard({
  booking,
  routePlan,
  teamName,
  draggable = true,
  onDragStart,
  onDragEnd
}: {
  booking: SchedulerBooking;
  routePlan?: DispatcherRoutePlan;
  teamName?: string;
  draggable?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  return (
    <div
      className="dispatcher-compact-booking"
      draggable={draggable}
      onDragStart={(event) => {
        if (!draggable) {
          return;
        }
        event.dataTransfer.setData("text/plain", booking.bookingId);
        onDragStart?.();
      }}
      onDragEnd={onDragEnd}
    >
      <div className="dispatcher-compact-booking__top">
        <strong>{booking.publicReference}</strong>
        <span>{formatDurationMinutes(getBookingDurationMinutes(booking))}</span>
      </div>
      <p>{booking.customerName}</p>
      <small>
        {routePlan
          ? `${formatMinutesAsClock(routePlan.plannedStartMinutes)} - ${formatMinutesAsClock(routePlan.plannedEndMinutes)} planned`
          : formatBookingTimeRange(booking)}
      </small>
      {routePlan?.startsAfterBookedTime && routePlan.requestedStartMinutes != null ? (
        <small>Scheduled time: {formatMinutesAsClock(routePlan.requestedStartMinutes)}</small>
      ) : null}
      {teamName ? <Badge tone="info">{teamName}</Badge> : null}
    </div>
  );
}

function DispatcherUnassignedRail({
  bookings,
  draggingBookingId,
  setDraggingBookingId
}: {
  bookings: SchedulerBooking[];
  draggingBookingId: string;
  setDraggingBookingId: (bookingId: string) => void;
}) {
  return (
    <aside className={`dispatcher-unassigned-rail ${draggingBookingId ? "is-dragging" : ""}`}>
      <div className="dispatcher-board-section-title">
        <strong>Unassigned</strong>
        <Badge tone={bookings.length ? "warning" : "success"}>{bookings.length}</Badge>
      </div>
      {bookings.length ? (
        <div className="dispatcher-compact-list">
          {bookings.map((booking) => (
            <DispatcherCompactBookingCard
              key={booking.bookingId}
              booking={booking}
              onDragStart={() => setDraggingBookingId(booking.bookingId)}
              onDragEnd={() => setDraggingBookingId("")}
            />
          ))}
        </div>
      ) : (
        <p className="card-copy">All visible bookings are assigned.</p>
      )}
    </aside>
  );
}

function getRoutePlanTimelinePosition(plan: DispatcherRoutePlan, workday: { startMinutes: number; endMinutes: number; timeZone: string }) {
  const totalMinutes = Math.max(60, workday.endMinutes - workday.startMinutes);
  const left = ((Math.max(workday.startMinutes, plan.plannedStartMinutes) - workday.startMinutes) / totalMinutes) * 100;
  const width = ((plan.plannedEndMinutes - plan.plannedStartMinutes) / totalMinutes) * 100;

  return {
    left: `${Math.max(0, Math.min(98, left))}%`,
    width: `${Math.max(8, Math.min(100 - Math.max(0, left), width))}%`
  };
}

function buildTeamRoutePlans(bookings: SchedulerBooking[], workday: { startMinutes: number; endMinutes: number; timeZone: string }) {
  let cursor = workday.startMinutes;
  return sortRouteBookings(bookings).map((booking, index) => {
    const requestedStartMinutes = getTimeZoneMinutes(booking.startsAt, workday.timeZone);
    const persistedStartMinutes = booking.assignment.plannedStartAt ? getTimeZoneMinutes(booking.assignment.plannedStartAt, workday.timeZone) : null;
    const persistedEndMinutes = booking.assignment.plannedEndAt ? getTimeZoneMinutes(booking.assignment.plannedEndAt, workday.timeZone) : null;
    const plannedStartMinutes = persistedStartMinutes ?? Math.max(cursor, requestedStartMinutes ?? cursor);
    const plannedEndMinutes = persistedEndMinutes ?? plannedStartMinutes + getBookingDurationMinutes(booking);
    cursor = plannedEndMinutes;
    return {
      booking,
      routeIndex: index,
      requestedStartMinutes,
      plannedStartMinutes,
      plannedEndMinutes,
      startsAfterBookedTime: requestedStartMinutes != null && plannedStartMinutes > requestedStartMinutes + 5,
      endsAfterWorkday: plannedEndMinutes > workday.endMinutes
    } satisfies DispatcherRoutePlan;
  });
}

function buildRoutePlansByTeamId(
  teams: SchedulerDayTeam[],
  assignedBookingsByTeamId: Map<string, SchedulerBooking[]>,
  workday: { startMinutes: number; endMinutes: number; timeZone: string }
) {
  return new Map(teams.map((team) => [team.id, buildTeamRoutePlans(assignedBookingsByTeamId.get(team.id) ?? [], workday)]));
}

function buildDispatcherRouteWarnings(
  teams: SchedulerDayTeam[],
  routePlansByTeamId: Map<string, DispatcherRoutePlan[]>,
  workday: { startMinutes: number; endMinutes: number; timeZone: string }
) {
  const warnings: DispatcherRouteWarning[] = [];
  for (const team of teams) {
    const plans = routePlansByTeamId.get(team.id) ?? [];
    if (!plans.length) {
      continue;
    }
    if (team.teamState !== "ready") {
      warnings.push({
        key: `${team.id}:state`,
        teamName: team.name,
        message: "Team is marked needs review but has assigned bookings.",
        tone: "warning"
      });
    }
    const lastPlan = plans[plans.length - 1];
    if (lastPlan && lastPlan.plannedEndMinutes > workday.endMinutes) {
      warnings.push({
        key: `${team.id}:over`,
        teamName: team.name,
        message: `Route ends at ${formatMinutesAsClock(lastPlan.plannedEndMinutes)}, after the ${formatMinutesAsClock(workday.endMinutes)} workday finish.`,
        tone: "danger"
      });
    }
    for (const plan of plans) {
      if (plan.startsAfterBookedTime && plan.requestedStartMinutes != null) {
        warnings.push({
          key: `${team.id}:${plan.booking.bookingId}:late`,
          teamName: team.name,
          message: `${plan.booking.publicReference} is planned for ${formatMinutesAsClock(plan.plannedStartMinutes)} instead of ${formatMinutesAsClock(
            plan.requestedStartMinutes
          )}.`,
          tone: "warning"
        });
      }
    }
  }
  return warnings;
}

function DispatcherHorizontalTimelineView({
  board,
  unassignedBookings,
  routePlansByTeamId,
  workday,
  ticks,
  draggingBookingId,
  setDraggingBookingId,
  onAssignToTeam,
  getMaidLabelById
}: {
  board: SchedulerDayBoard;
  unassignedBookings: SchedulerBooking[];
  routePlansByTeamId: Map<string, DispatcherRoutePlan[]>;
  workday: { startMinutes: number; endMinutes: number; timeZone: string };
  ticks: number[];
  draggingBookingId: string;
  setDraggingBookingId: (bookingId: string) => void;
  onAssignToTeam: (teamId: string, bookingId?: string, sequenceIndex?: number) => void;
  getMaidLabelById: (staffId: string, fallback?: string) => string;
}) {
  return (
    <div className="dispatcher-board-grid">
      <DispatcherUnassignedRail bookings={unassignedBookings} draggingBookingId={draggingBookingId} setDraggingBookingId={setDraggingBookingId} />
      <div className="dispatcher-horizontal-board">
        <div className="dispatcher-timeline-header">
          <span />
          <div className="dispatcher-time-scale">
            {ticks.map((tick) => (
              <span key={tick}>{formatMinutesAsClock(tick)}</span>
            ))}
          </div>
        </div>
        {board.teams.length ? (
          board.teams.map((team) => {
            const routePlans = routePlansByTeamId.get(team.id) ?? [];
            const totalMinutes = Math.max(60, workday.endMinutes - workday.startMinutes);
            const getDropSequenceIndex = (event: DragEvent<HTMLDivElement>) => {
              if (!routePlans.length) {
                return 0;
              }
              const rect = event.currentTarget.getBoundingClientRect();
              const pointerRatio = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 1;
              const pointerMinutes = workday.startMinutes + Math.max(0, Math.min(1, pointerRatio)) * totalMinutes;
              const nextIndex = routePlans.findIndex((plan) => pointerMinutes <= plan.plannedStartMinutes + (plan.plannedEndMinutes - plan.plannedStartMinutes) / 2);
              return nextIndex === -1 ? routePlans.length : nextIndex;
            };
            return (
              <div className="dispatcher-horizontal-lane" key={team.id}>
                <div className="dispatcher-team-label">
                  <strong>{team.name}</strong>
                  <span>{team.memberStaffIds.map((staffId, index) => getMaidLabelById(staffId, team.memberNames[index] ?? "")).join(" | ")}</span>
                  <small>{team.actualTeamSize}/{team.targetTeamSize} maids</small>
                </div>
                <div
                  className={`dispatcher-horizontal-lane__timeline ${draggingBookingId ? "is-drop-ready" : ""}`}
                  onDragOver={(event) => {
                    if (draggingBookingId) {
                      event.preventDefault();
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    onAssignToTeam(team.id, event.dataTransfer.getData("text/plain") || draggingBookingId, getDropSequenceIndex(event));
                  }}
                >
                  {routePlans.map((plan) => {
                    const { booking } = plan;
                    const position = getRoutePlanTimelinePosition(plan, workday);
                    return (
                      <div
                        key={booking.bookingId}
                        className={`dispatcher-timeline-block ${plan.endsAfterWorkday ? "is-over-capacity" : ""} ${
                          plan.startsAfterBookedTime ? "is-shifted" : ""
                        }`}
                        draggable
                        style={{ left: position.left, width: position.width }}
                        onDragStart={(event) => {
                          event.dataTransfer.setData("text/plain", booking.bookingId);
                          setDraggingBookingId(booking.bookingId);
                        }}
                        onDragEnd={() => setDraggingBookingId("")}
                        title={`${booking.publicReference} - ${booking.customerName}`}
                      >
                        <strong>{booking.publicReference}</strong>
                        <span>{booking.customerName}</span>
                        <small>
                          {formatMinutesAsClock(plan.plannedStartMinutes)} - {formatMinutesAsClock(plan.plannedEndMinutes)}
                        </small>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        ) : (
          <p className="card-copy">Build day teams before using the timeline.</p>
        )}
      </div>
    </div>
  );
}

function DispatcherVerticalTimelineView({
  board,
  unassignedBookings,
  routePlansByTeamId,
  workday,
  ticks,
  draggingBookingId,
  setDraggingBookingId,
  onAssignToTeam,
  getMaidLabelById
}: {
  board: SchedulerDayBoard;
  unassignedBookings: SchedulerBooking[];
  routePlansByTeamId: Map<string, DispatcherRoutePlan[]>;
  workday: { startMinutes: number; endMinutes: number; timeZone: string };
  ticks: number[];
  draggingBookingId: string;
  setDraggingBookingId: (bookingId: string) => void;
  onAssignToTeam: (teamId: string, bookingId?: string, sequenceIndex?: number) => void;
  getMaidLabelById: (staffId: string, fallback?: string) => string;
}) {
  const totalMinutes = Math.max(60, workday.endMinutes - workday.startMinutes);
  const timelineHeight = Math.max(520, totalMinutes * 1.1);

  return (
    <div className="dispatcher-board-grid">
      <DispatcherUnassignedRail bookings={unassignedBookings} draggingBookingId={draggingBookingId} setDraggingBookingId={setDraggingBookingId} />
      <div className="dispatcher-vertical-board" style={{ "--team-count": Math.max(1, board.teams.length) } as CSSProperties}>
        <div className="dispatcher-vertical-header" />
        {board.teams.map((team) => (
          <div className="dispatcher-vertical-team-heading" key={team.id}>
            <strong>{team.name}</strong>
            <span>{team.memberStaffIds.map((staffId, index) => getMaidLabelById(staffId, team.memberNames[index] ?? "")).join(" | ")}</span>
          </div>
        ))}
        <div className="dispatcher-vertical-time-axis" style={{ height: timelineHeight }}>
          {ticks.map((tick) => (
            <span key={tick} style={{ top: `${((tick - workday.startMinutes) / totalMinutes) * 100}%` }}>
              {formatMinutesAsClock(tick)}
            </span>
          ))}
        </div>
        {board.teams.map((team) => {
          const routePlans = routePlansByTeamId.get(team.id) ?? [];
          const getDropSequenceIndex = (event: DragEvent<HTMLDivElement>) => {
            if (!routePlans.length) {
              return 0;
            }
            const rect = event.currentTarget.getBoundingClientRect();
            const pointerRatio = rect.height > 0 ? (event.clientY - rect.top) / rect.height : 1;
            const pointerMinutes = workday.startMinutes + Math.max(0, Math.min(1, pointerRatio)) * totalMinutes;
            const nextIndex = routePlans.findIndex((plan) => pointerMinutes <= plan.plannedStartMinutes + (plan.plannedEndMinutes - plan.plannedStartMinutes) / 2);
            return nextIndex === -1 ? routePlans.length : nextIndex;
          };
          return (
            <div
              key={team.id}
              className={`dispatcher-vertical-team-column ${draggingBookingId ? "is-drop-ready" : ""}`}
              style={{ height: timelineHeight }}
              onDragOver={(event) => {
                if (draggingBookingId) {
                  event.preventDefault();
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                onAssignToTeam(team.id, event.dataTransfer.getData("text/plain") || draggingBookingId, getDropSequenceIndex(event));
              }}
            >
              {routePlans.map((plan) => {
                const { booking } = plan;
                const top = ((Math.max(workday.startMinutes, plan.plannedStartMinutes) - workday.startMinutes) / totalMinutes) * 100;
                const height = ((plan.plannedEndMinutes - plan.plannedStartMinutes) / totalMinutes) * 100;
                return (
                  <div
                    key={booking.bookingId}
                    className={`dispatcher-vertical-block ${plan.endsAfterWorkday ? "is-over-capacity" : ""} ${
                      plan.startsAfterBookedTime ? "is-shifted" : ""
                    }`}
                    draggable
                    style={{ top: `${Math.max(0, Math.min(96, top))}%`, height: `${Math.max(8, height)}%` }}
                    onDragStart={(event) => {
                      event.dataTransfer.setData("text/plain", booking.bookingId);
                      setDraggingBookingId(booking.bookingId);
                    }}
                    onDragEnd={() => setDraggingBookingId("")}
                  >
                    <strong>{booking.publicReference}</strong>
                    <span>{booking.customerName}</span>
                    <small>
                      {formatMinutesAsClock(plan.plannedStartMinutes)} - {formatMinutesAsClock(plan.plannedEndMinutes)}
                    </small>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DispatcherListView({
  board,
  unassignedBookings,
  routePlansByTeamId,
  draggingBookingId,
  setDraggingBookingId,
  onAssignToTeam,
  getMaidLabelById
}: {
  board: SchedulerDayBoard;
  unassignedBookings: SchedulerBooking[];
  routePlansByTeamId: Map<string, DispatcherRoutePlan[]>;
  draggingBookingId: string;
  setDraggingBookingId: (bookingId: string) => void;
  onAssignToTeam: (teamId: string, bookingId?: string, sequenceIndex?: number) => void;
  getMaidLabelById: (staffId: string, fallback?: string) => string;
}) {
  return (
    <div className="dispatcher-board-grid">
      <DispatcherUnassignedRail bookings={unassignedBookings} draggingBookingId={draggingBookingId} setDraggingBookingId={setDraggingBookingId} />
      <div className="dispatcher-route-list">
        {board.teams.length ? (
          board.teams.map((team) => {
            const routePlans = routePlansByTeamId.get(team.id) ?? [];
            const assignedBookings = routePlans.map((plan) => plan.booking);
            return (
              <section
                className={`dispatcher-route-team ${draggingBookingId ? "is-drop-ready" : ""}`}
                key={team.id}
                onDragOver={(event) => {
                  if (draggingBookingId) {
                    event.preventDefault();
                  }
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  onAssignToTeam(team.id, event.dataTransfer.getData("text/plain") || draggingBookingId, assignedBookings.length);
                }}
              >
                <div className="dispatcher-route-team__header">
                  <div>
                    <strong>{team.name}</strong>
                    <p>{team.memberStaffIds.map((staffId, index) => getMaidLabelById(staffId, team.memberNames[index] ?? "")).join(" | ")}</p>
                  </div>
                  <Badge tone={team.teamState === "ready" ? "success" : "warning"}>{assignedBookings.length} stops</Badge>
                </div>
                {assignedBookings.length ? (
                  <ol className="dispatcher-stop-list">
                    {routePlans.map((plan, index) => (
                      <li
                        key={plan.booking.bookingId}
                        className={draggingBookingId ? "is-drop-ready" : ""}
                        onDragOver={(event) => {
                          if (draggingBookingId) {
                            event.preventDefault();
                          }
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          onAssignToTeam(team.id, event.dataTransfer.getData("text/plain") || draggingBookingId, index);
                        }}
                      >
                        <span>{index + 1}</span>
                        <DispatcherCompactBookingCard
                          booking={plan.booking}
                          routePlan={plan}
                          draggable
                          onDragStart={() => setDraggingBookingId(plan.booking.bookingId)}
                          onDragEnd={() => setDraggingBookingId("")}
                        />
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="card-copy">Drop a booking here or use the assignment dropdown.</p>
                )}
              </section>
            );
          })
        ) : (
          <p className="card-copy">Build day teams before assigning routes.</p>
        )}
      </div>
    </div>
  );
}

function DispatcherMapView({
  board,
  assignedBookingsByTeamId,
  unassignedBookings,
  routePlansByTeamId,
  draggingBookingId,
  setDraggingBookingId,
  onAssignToTeam
}: {
  board: SchedulerDayBoard;
  assignedBookingsByTeamId: Map<string, SchedulerBooking[]>;
  unassignedBookings: SchedulerBooking[];
  routePlansByTeamId: Map<string, DispatcherRoutePlan[]>;
  draggingBookingId: string;
  setDraggingBookingId: (bookingId: string) => void;
  onAssignToTeam: (teamId: string, bookingId?: string, sequenceIndex?: number) => void;
}) {
  const visibleBookings = board.bookings;
  const mappableBookings = useMemo(
    () => visibleBookings.filter((booking) => `${booking.serviceAddress || ""}${booking.postalCode || ""}`.trim()),
    [visibleBookings]
  );
  const [selectedBookingId, setSelectedBookingId] = useState("");

  useEffect(() => {
    if (!mappableBookings.length) {
      setSelectedBookingId("");
      return;
    }
    if (!mappableBookings.some((booking) => booking.bookingId === selectedBookingId)) {
      setSelectedBookingId(mappableBookings[0]?.bookingId ?? "");
    }
  }, [mappableBookings, selectedBookingId]);

  const selectedBooking = mappableBookings.find((booking) => booking.bookingId === selectedBookingId) ?? mappableBookings[0] ?? null;
  const selectedTeam = selectedBooking?.assignment.scheduleDayTeamId
    ? board.teams.find((entry) => entry.id === selectedBooking.assignment.scheduleDayTeamId)
    : null;
  const routePlanByBookingId = useMemo(() => {
    const next = new Map<string, DispatcherRoutePlan>();
    for (const plans of routePlansByTeamId.values()) {
      for (const plan of plans) {
        next.set(plan.booking.bookingId, plan);
      }
    }
    return next;
  }, [routePlansByTeamId]);
  const selectedRoutePlan = selectedBooking ? routePlanByBookingId.get(selectedBooking.bookingId) : null;
  const selectedAddressQuery = selectedBooking ? selectedBooking.serviceAddress || selectedBooking.postalCode : "";
  const selectedMapsUrl = selectedAddressQuery
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedAddressQuery)}`
    : "";
  const selectedMapEmbedUrl = selectedAddressQuery
    ? `https://www.google.com/maps?q=${encodeURIComponent(selectedAddressQuery)}&output=embed`
    : "";

  return (
    <div className="dispatcher-map-layout">
      <div className="dispatcher-map-routes">
        <DispatcherUnassignedRail bookings={unassignedBookings} draggingBookingId={draggingBookingId} setDraggingBookingId={setDraggingBookingId} />
        {board.teams.map((team) => {
          const assignedBookings = sortRouteBookings(assignedBookingsByTeamId.get(team.id) ?? []);
          return (
            <section
              className={`dispatcher-map-team ${draggingBookingId ? "is-drop-ready" : ""}`}
              key={team.id}
              onDragOver={(event) => {
                if (draggingBookingId) {
                  event.preventDefault();
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                onAssignToTeam(team.id, event.dataTransfer.getData("text/plain") || draggingBookingId);
              }}
            >
              <strong>{team.name}</strong>
              <p>{assignedBookings.length} assigned stops</p>
            </section>
          );
        })}
      </div>
      <div className="dispatcher-map-canvas">
        <div className="dispatcher-map-note">
          <MapPinned size={18} aria-hidden="true" />
          <span>Select a booking to center the map point. Drag booking cards to team lanes on the left to assign routes.</span>
        </div>
        <div className="dispatcher-map-embed-shell">
          {selectedMapEmbedUrl ? (
            <iframe
              className="dispatcher-map-embed"
              title={`Map for ${selectedBooking?.publicReference ?? "selected booking"}`}
              src={selectedMapEmbedUrl}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div className="dispatcher-map-empty">
              <MapPinned size={28} aria-hidden="true" />
              <strong>No mappable booking address</strong>
              <p>Add a service address to a visible booking to show the map point.</p>
            </div>
          )}
          {selectedBooking ? (
            <div className="dispatcher-map-detail-card">
              <div>
                <strong>{selectedBooking.publicReference}</strong>
                <span>{selectedTeam?.name ?? "Unassigned"}</span>
              </div>
              <p>{selectedBooking.customerName}</p>
              <p>{selectedBooking.serviceAddress || selectedBooking.postalCode || "No service address"}</p>
              <small>
                {selectedRoutePlan
                  ? `${formatMinutesAsClock(selectedRoutePlan.plannedStartMinutes)} - ${formatMinutesAsClock(selectedRoutePlan.plannedEndMinutes)} planned`
                  : formatBookingTimeRange(selectedBooking)}
                {selectedBooking.arrivalWindowLabel ? ` | ${selectedBooking.arrivalWindowLabel}` : ""}
              </small>
              {selectedMapsUrl ? (
                <a className="button button--secondary" href={selectedMapsUrl} target="_blank" rel="noreferrer">
                  Open in Google Maps
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="dispatcher-map-address-list" aria-label="Map booking points">
          {mappableBookings.map((booking) => {
            const team = booking.assignment.scheduleDayTeamId ? board.teams.find((entry) => entry.id === booking.assignment.scheduleDayTeamId) : null;
            return (
              <button
                key={booking.bookingId}
                type="button"
                className={`dispatcher-map-address-card ${team ? "is-assigned" : "is-unassigned"} ${
                  booking.bookingId === selectedBooking?.bookingId ? "is-active" : ""
                }`}
                onClick={() => setSelectedBookingId(booking.bookingId)}
              >
                <div>
                  <strong>{booking.publicReference}</strong>
                  <span>{team?.name ?? "Unassigned"}</span>
                </div>
                <p>{booking.customerName}</p>
                <p>{booking.serviceAddress || booking.postalCode || "No service address"}</p>
                <small>
                  {routePlanByBookingId.has(booking.bookingId)
                    ? `${formatMinutesAsClock(routePlanByBookingId.get(booking.bookingId)!.plannedStartMinutes)} planned`
                    : formatBookingTimeRange(booking)}
                </small>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function DispatcherPage() {
  return <SchedulerWorkspace mode="dispatcher" />;
}

export function SchedulerPage() {
  return <SchedulerWorkspace mode="setup" />;
}

function SchedulerWorkspace({ mode }: { mode: "dispatcher" | "setup" }) {
  const isDispatcher = mode === "dispatcher";
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(getTodayIsoDate());
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [homeTeamDraft, setHomeTeamDraft] = useState<HomeTeamDraft>(createEmptyHomeTeamDraft());
  const [dayTeamDraft, setDayTeamDraft] = useState<DayTeamDraft>(createEmptyDayTeamDraft());
  const [pageMessage, setPageMessage] = useState("");
  const [pageError, setPageError] = useState("");
  const [pendingBookingId, setPendingBookingId] = useState("");
  const [draggingBookingId, setDraggingBookingId] = useState("");
  const [clearUnlockedOnAutoAssign, setClearUnlockedOnAutoAssign] = useState(true);
  const [dispatcherView, setDispatcherView] = useState<DispatcherView>("horizontal");
  const [routeStartDrafts, setRouteStartDrafts] = useState<Record<string, string>>({});

  const locationsQuery = useQuery({
    queryKey: ["scheduler-locations"],
    queryFn: () => apiFetch<{ items: LocationItem[] }>("/api/settings/locations")
  });
  const staffQuery = useQuery({
    queryKey: ["scheduler-staff"],
    queryFn: () => apiFetch<{ items: StaffSettingsItem[] }>("/api/settings/staff")
  });
  const staffTypesQuery = useQuery({
    queryKey: ["scheduler-staff-types"],
    queryFn: () => apiFetch<{ items: StaffTypeSettingsItem[] }>("/api/settings/staff-types")
  });

  const locations = (locationsQuery.data?.items ?? [])
    .filter((item) => isActiveFlag(item.active))
    .sort((left, right) => String(left.name ?? "").localeCompare(String(right.name ?? "")));

  useEffect(() => {
    if (!selectedLocationId && locations.length) {
      setSelectedLocationId(String(locations[0]?.id ?? ""));
    }
  }, [locations, selectedLocationId]);

  const boardQuery = useQuery({
    queryKey: ["scheduler-board", selectedLocationId, selectedDate],
    queryFn: () =>
      apiFetch<SchedulerDayBoard>(
        `/api/scheduler/day?locationId=${encodeURIComponent(selectedLocationId)}&date=${encodeURIComponent(selectedDate)}`
      ),
    enabled: Boolean(selectedLocationId)
  });

  const board = boardQuery.data;
  const boardKey = ["scheduler-board", selectedLocationId, selectedDate];
  const effectiveError = pageError || (boardQuery.error instanceof Error ? boardQuery.error.message : "");

  const resetHomeTeamDraft = () => setHomeTeamDraft(createEmptyHomeTeamDraft());
  const resetDayTeamDraft = () => setDayTeamDraft(createEmptyDayTeamDraft());

  useEffect(() => {
    resetHomeTeamDraft();
    resetDayTeamDraft();
    setPageMessage("");
    setPageError("");
  }, [selectedLocationId, selectedDate]);

  useEffect(() => {
    setRouteStartDrafts(Object.fromEntries((board?.teams ?? []).map((team) => [team.id, team.routeStartTime ?? ""])));
  }, [board?.teams]);

  const staffTypeById = new Map(
    (staffTypesQuery.data?.items ?? []).map((item) => [
      String(item.id ?? ""),
      (item.directoryCategory === "drivers" || item.directoryCategory === "office" ? item.directoryCategory : "maids") as StaffDirectoryCategory
    ])
  );
  const staffById = new Map((staffQuery.data?.items ?? []).map((staff) => [String(staff.id ?? ""), staff]));
  const getMaidLabelById = (staffId: string, fallback = "") => {
    const staff = staffById.get(staffId);
    return staff ? getMaidLabel(staff) : fallback || staffId;
  };

  const eligibleStaff = (staffQuery.data?.items ?? []).filter(
    (staff) => isActiveFlag(staff.active) && isLocationEligible(staff, selectedLocationId)
  );
  const maidStaff = eligibleStaff
    .filter((staff) => resolveDirectoryCategory(staff, staffTypeById) === "maids")
    .sort((left, right) => getMaidLabel(left).localeCompare(getMaidLabel(right)));
  const availableMaidChoices = (board?.availableMaids ?? []).slice().sort((left, right) => left.name.localeCompare(right.name));
  async function writeBoardMessage(nextBoard: SchedulerDayBoard, message: string) {
    queryClient.setQueryData(boardKey, nextBoard);
    setPageMessage(message);
    setPageError("");
  }

  async function refetchBoard(message: string) {
    await queryClient.invalidateQueries({ queryKey: boardKey });
    setPageMessage(message);
    setPageError("");
  }

  const bootstrapMutation = useMutation({
    mutationFn: () =>
      apiFetch<SchedulerDayBoard>("/api/scheduler/day/bootstrap", {
        method: "POST",
        body: JSON.stringify({ locationId: selectedLocationId, date: selectedDate, mode: "from_home_teams" })
      }),
    onSuccess: (nextBoard) => writeBoardMessage(nextBoard, "Staffing board rebuilt from saved teams."),
    onError: (error) => setPageError(error instanceof Error ? error.message : "Unable to rebuild the staffing board.")
  });

  const autoAssignMutation = useMutation({
    mutationFn: () =>
      apiFetch<SchedulerDayBoard>("/api/scheduler/day/auto-assign", {
        method: "POST",
        body: JSON.stringify({
          locationId: selectedLocationId,
          date: selectedDate,
          preserveLocked: true,
          clearUnlocked: clearUnlockedOnAutoAssign
        })
      }),
    onSuccess: (nextBoard) => writeBoardMessage(nextBoard, "Bookings auto-assigned to ready teams."),
    onError: (error) => setPageError(error instanceof Error ? error.message : "Unable to auto-assign bookings.")
  });

  const saveHomeTeamMutation = useMutation({
    mutationFn: (payload: SchedulerHomeTeamInput & { id?: string }) =>
      apiFetch(
        payload.id ? `/api/scheduler/home-teams/${payload.id}` : "/api/scheduler/home-teams",
        {
          method: payload.id ? "PUT" : "POST",
          body: JSON.stringify(payload)
        }
      ),
    onSuccess: async (_result, payload) => {
      resetHomeTeamDraft();
      await refetchBoard(payload.id ? "Saved home team updated." : "Saved home team created.");
    },
    onError: (error) => setPageError(error instanceof Error ? error.message : "Unable to save the home team.")
  });

  const deleteHomeTeamMutation = useMutation({
    mutationFn: (teamId: string) => apiFetch(`/api/scheduler/home-teams/${teamId}`, { method: "DELETE" }),
    onSuccess: async () => {
      resetHomeTeamDraft();
      await refetchBoard("Saved home team removed.");
    },
    onError: (error) => setPageError(error instanceof Error ? error.message : "Unable to delete the home team.")
  });

  const saveDayTeamMutation = useMutation({
    mutationFn: (payload: SchedulerDayTeamInput & { id?: string }) =>
      apiFetch<SchedulerDayBoard>(
        payload.id ? `/api/scheduler/day/teams/${payload.id}` : "/api/scheduler/day/teams",
        {
          method: payload.id ? "PUT" : "POST",
          body: JSON.stringify(
            payload.id
              ? {
                  name: payload.name,
                  memberStaffIds: payload.memberStaffIds,
                  driverStaffId: payload.driverStaffId,
                  targetTeamSize: payload.targetTeamSize,
                  teamState: payload.teamState,
                  routeStartTime: payload.routeStartTime,
                  notes: payload.notes
                }
              : payload
          )
        }
      ),
    onSuccess: (nextBoard, payload) => {
      resetDayTeamDraft();
      return writeBoardMessage(nextBoard, payload.id ? "Day team updated." : "Day team created.");
    },
    onError: (error) => setPageError(error instanceof Error ? error.message : "Unable to save the day team.")
  });

  const deleteDayTeamMutation = useMutation({
    mutationFn: (teamId: string) => apiFetch<SchedulerDayBoard>(`/api/scheduler/day/teams/${teamId}`, { method: "DELETE" }),
    onSuccess: (nextBoard) => {
      resetDayTeamDraft();
      return writeBoardMessage(nextBoard, "Day team removed.");
    },
    onError: (error) => setPageError(error instanceof Error ? error.message : "Unable to delete the day team.")
  });

  const publishTeamRouteMutation = useMutation({
    mutationFn: (teamId: string) => apiFetch<SchedulerDayBoard>(`/api/scheduler/day/teams/${teamId}/publish`, { method: "POST" }),
    onSuccess: (nextBoard) => writeBoardMessage(nextBoard, "Team route published for staff."),
    onError: (error) => setPageError(error instanceof Error ? error.message : "Unable to publish the team route.")
  });

  const staffOverrideMutation = useMutation({
    mutationFn: (overrides: Array<{ staffId: string; availabilityStatus: "available" | "off"; note?: string }>) =>
      apiFetch<SchedulerDayBoard>("/api/scheduler/day/staff-overrides", {
        method: "PUT",
        body: JSON.stringify({ locationId: selectedLocationId, date: selectedDate, overrides })
      }),
    onSuccess: (nextBoard) => writeBoardMessage(nextBoard, "Daily staff overrides updated."),
    onError: (error) => setPageError(error instanceof Error ? error.message : "Unable to update staff overrides.")
  });

  const bookingAssignmentMutation = useMutation({
    mutationFn: (input: { bookingId: string; payload: SchedulerDayBookingAssignmentUpdateInput }) =>
      apiFetch<SchedulerDayBoard>(`/api/scheduler/day/bookings/${input.bookingId}`, {
        method: "PUT",
        body: JSON.stringify(input.payload)
      }),
    onMutate: (input) => {
      setPendingBookingId(input.bookingId);
    },
    onSuccess: (nextBoard) => writeBoardMessage(nextBoard, "Booking assignment saved."),
    onError: (error) => setPageError(error instanceof Error ? error.message : "Unable to save the booking assignment."),
    onSettled: () => setPendingBookingId("")
  });

  const submitHomeTeam = () => {
    if (!selectedLocationId) {
      setPageError("Choose a location first.");
      return;
    }
    saveHomeTeamMutation.mutate({
      id: homeTeamDraft.id || undefined,
      locationId: selectedLocationId,
      name: homeTeamDraft.name.trim(),
      defaultDriverStaffId: homeTeamDraft.defaultDriverStaffId || null,
      leadStaffId: homeTeamDraft.leadStaffId || homeTeamDraft.memberStaffIds[0] || "",
      memberStaffIds: homeTeamDraft.memberStaffIds,
      active: true
    });
  };

  const submitDayTeam = () => {
    if (!selectedLocationId) {
      setPageError("Choose a location first.");
      return;
    }
    saveDayTeamMutation.mutate({
      id: dayTeamDraft.id || undefined,
      locationId: selectedLocationId,
      date: selectedDate,
      name: dayTeamDraft.name.trim(),
      driverStaffId: dayTeamDraft.driverStaffId || null,
      memberStaffIds: dayTeamDraft.memberStaffIds,
      targetTeamSize: Number(dayTeamDraft.targetTeamSize || dayTeamDraft.memberStaffIds.length || 1),
      teamState: dayTeamDraft.teamState,
      routeStartTime: dayTeamDraft.routeStartTime || null,
      notes: dayTeamDraft.notes,
      sourceMaidTeamId: null
    });
  };

  const saveTeamRouteStart = (team: SchedulerDayTeam) => {
    saveDayTeamMutation.mutate({
      id: team.id,
      locationId: selectedLocationId,
      date: selectedDate,
      name: team.name,
      driverStaffId: team.driverStaffId,
      memberStaffIds: team.memberStaffIds,
      targetTeamSize: team.targetTeamSize,
      teamState: team.teamState,
      routeStartTime: routeStartDrafts[team.id] || null,
      notes: team.notes,
      sourceMaidTeamId: team.sourceMaidTeamId
    });
  };

  const currentManualOverrides = deriveManualOverrides(board);
  const unassignedBookings = (board?.bookings ?? []).filter((booking) => !booking.assignment.scheduleDayTeamId);
  const assignedBookingsByTeamId = new Map<string, SchedulerDayBoard["bookings"]>();
  for (const booking of board?.bookings ?? []) {
    const teamId = booking.assignment.scheduleDayTeamId;
    if (!teamId) {
      continue;
    }
    const current = assignedBookingsByTeamId.get(teamId) ?? [];
    current.push(booking);
    assignedBookingsByTeamId.set(teamId, current);
  }
  const readyTeamCount = (board?.teams ?? []).filter((team) => team.teamState === "ready").length;
  const bookingsListUrl = `/admin/bookings?date=${encodeURIComponent(selectedDate)}&locationId=${encodeURIComponent(
    selectedLocationId || "all"
  )}`;
  const selectedLocation = locations.find((location) => location.id === selectedLocationId);
  const selectedLocationName = selectedLocation?.name || "selected location";
  const visibility = board?.bookingVisibility;
  const dispatcherWorkday = useMemo(
    () => resolveDispatcherWorkday(selectedLocation, selectedDate, board?.bookings ?? []),
    [board?.bookings, selectedDate, selectedLocation]
  );
  const dispatcherTimelineTicks = useMemo(
    () => buildTimelineTicks(dispatcherWorkday.startMinutes, dispatcherWorkday.endMinutes),
    [dispatcherWorkday.endMinutes, dispatcherWorkday.startMinutes]
  );
  const routePlansByTeamId = buildRoutePlansByTeamId(board?.teams ?? [], assignedBookingsByTeamId, dispatcherWorkday);
  const dispatcherRouteWarnings = buildDispatcherRouteWarnings(board?.teams ?? [], routePlansByTeamId, dispatcherWorkday);
  const warningCount = (board?.day.warnings.length ?? 0) + (board?.syncWarnings.length ?? 0) + dispatcherRouteWarnings.length;

  const assignDraggedBookingToTeam = (teamId: string, bookingId = draggingBookingId, sequenceIndex?: number) => {
    if (!bookingId) {
      return;
    }
    const booking = board?.bookings.find((entry) => entry.bookingId === bookingId);
    bookingAssignmentMutation.mutate({
      bookingId,
      payload: {
        scheduleDayTeamId: teamId,
        isLocked: true,
        sequenceIndex,
        note: booking?.assignment.manualNote ?? ""
      }
    });
    setDraggingBookingId("");
  };

  const saveOverrideState = (staffId: string, nextState: "available" | "off" | "reset") => {
    const overrides = new Map(currentManualOverrides);
    if (nextState === "reset") {
      overrides.delete(staffId);
    } else {
      overrides.set(staffId, nextState);
    }

    staffOverrideMutation.mutate(
      [...overrides.entries()]
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([overrideStaffId, availabilityStatus]) => ({ staffId: overrideStaffId, availabilityStatus }))
    );
  };

  return (
    <OpsPageShell>
      <OpsHeader
        eyebrow={isDispatcher ? "Daily dispatch" : "Staff setup"}
        title={isDispatcher ? "Dispatcher" : "Teams"}
        description={
          isDispatcher
            ? "Work one service date at a time: review visible bookings, build day teams, and assign routes."
            : "Manage saved cleaning teams. Dispatcher copies these teams into a selected service date when you build the day."
        }
        actions={
          isDispatcher ? (
            <Link className="button button--secondary" to="/admin/settings/teams">
              Open Teams
            </Link>
          ) : (
            <Link className="button button--secondary" to="/admin/dispatcher">
              Open Dispatcher
            </Link>
          )
        }
      />

      <div className={isDispatcher ? "scheduler-dispatcher-sticky" : ""}>
      <Card>
        {isDispatcher ? (
          <div className="dispatcher-command-card">
            <div className="dispatcher-command-card__main">
              <div className="dispatcher-command-card__fields">
                <Field label="Date">
                  <DateInput value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} />
                </Field>
                <Field label="Location">
                  <select value={selectedLocationId} onChange={(event) => setSelectedLocationId(event.target.value)}>
                    <option value="">Choose location</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name || "Location"}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="dispatcher-command-card__actions">
                <Button
                  variant="secondary"
                  disabled={!selectedLocationId || boardQuery.isFetching}
                  onClick={() => void refetchBoard("Dispatcher board refreshed.")}
                >
                  <RefreshCw size={16} aria-hidden="true" /> Refresh
                </Button>
                <Button
                  variant="secondary"
                  disabled={!selectedLocationId || bootstrapMutation.isPending}
                  onClick={() => bootstrapMutation.mutate()}
                >
                  <WandSparkles size={16} aria-hidden="true" /> Build teams
                </Button>
                <Button
                  disabled={!selectedLocationId || autoAssignMutation.isPending}
                  onClick={() => autoAssignMutation.mutate()}
                >
                  Auto-assign
                </Button>
              </div>
            </div>
            <div className="dispatcher-workflow" aria-label="Dispatcher workflow">
              <span className="dispatcher-workflow__step"><strong>1</strong> Pick date and location</span>
              <span className="dispatcher-workflow__step"><strong>2</strong> Review visible bookings</span>
              <span className="dispatcher-workflow__step"><strong>3</strong> Build or edit day teams</span>
              <span className="dispatcher-workflow__step"><strong>4</strong> Assign and lock routes</span>
            </div>
            <div className="dispatcher-command-card__options">
              <label className="dispatcher-reset-toggle">
                <input
                  type="checkbox"
                  checked={clearUnlockedOnAutoAssign}
                  onChange={(event) => setClearUnlockedOnAutoAssign(event.target.checked)}
                />
                Reset unlocked assignments before auto-assign
              </label>
              <span>Locked assignments stay protected.</span>
            </div>
            <div className={`dispatcher-command-card__views ${board?.teams.length ? "" : "dispatcher-command-card__views--locked"}`}>
              <div>
                <strong>Dispatch view</strong>
                <span>
                  {board?.teams.length
                    ? `Workday ${formatMinutesAsClock(dispatcherWorkday.startMinutes)} - ${formatMinutesAsClock(dispatcherWorkday.endMinutes)}`
                    : "Build day teams first to unlock Horizontal, Vertical, Map, and List views."}
                </span>
              </div>
              <DispatcherViewSwitcher value={dispatcherView} onChange={setDispatcherView} disabled={!board?.teams.length} />
            </div>
          </div>
        ) : (
          <div className="scheduler-toolbar scheduler-toolbar--setup">
            <Field label="Location">
              <select value={selectedLocationId} onChange={(event) => setSelectedLocationId(event.target.value)}>
                <option value="">Choose location</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name || "Location"}
                  </option>
                ))}
              </select>
            </Field>
            <p className="card-copy" style={{ margin: 0 }}>
              Saved teams are location templates. They do not create or assign bookings until Dispatcher builds a selected day.
            </p>
          </div>
        )}
      </Card>
      </div>

      {effectiveError ? (
        <Card>
          <Badge tone="danger">Error</Badge>
          <p className="card-copy" style={{ marginTop: "0.75rem", marginBottom: 0 }}>
            {effectiveError}
          </p>
        </Card>
      ) : null}
      {pageMessage ? (
        <Card>
          <Badge tone="success">Saved</Badge>
          <p className="card-copy" style={{ marginTop: "0.75rem", marginBottom: 0 }}>
            {pageMessage}
          </p>
        </Card>
      ) : null}

      {boardQuery.isLoading ? <p className="card-copy">Loading scheduler board...</p> : null}
      {board ? (
        <>
          {!isDispatcher ? (
          <div className="scheduler-setup-layout">
            <Card title="Saved Cleaning Teams" eyebrow="Team templates">
              <p className="card-copy">These are the usual maid teams for this location. Dispatcher copies them into a service date when you build the day.</p>
              <div className="booking-list">
                {board.homeTeams.length ? (
                  board.homeTeams.map((team) => (
                    <div className="booking-list-item scheduler-home-team-card" key={team.id} style={{ cursor: "default" }}>
                      <div className="booking-list-item__top">
                        <strong>{team.name}</strong>
                        <Badge tone="info">{team.members.length} maids</Badge>
                      </div>
                      <div className="scheduler-team-members">
                        {team.members.map((member) => (
                          <span key={member.staffId} className={member.isLead ? "is-lead" : ""}>
                            {getMaidLabelById(member.staffId, member.name)}
                          </span>
                        ))}
                      </div>
                      <div className="customer-directory-actions" style={{ marginTop: "0.75rem" }}>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            setHomeTeamDraft({
                              id: team.id,
                              name: team.name,
                              defaultDriverStaffId: "",
                              leadStaffId: team.members.find((member) => member.isLead)?.staffId ?? team.members[0]?.staffId ?? "",
                              memberStaffIds: team.members.map((member) => member.staffId)
                            })
                          }
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          disabled={deleteHomeTeamMutation.isPending}
                          onClick={() => {
                            if (window.confirm(`Delete ${team.name}?`)) {
                              deleteHomeTeamMutation.mutate(team.id);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                <p className="card-copy">No saved cleaning teams yet.</p>
                )}
              </div>
            </Card>

            <Card title={homeTeamDraft.id ? "Edit Cleaning Team" : "New Cleaning Team"} eyebrow="Saved team setup">
              <div className="scheduler-team-builder">
                <div className="scheduler-form-grid">
                  <Field label="Team name">
                    <input
                      value={homeTeamDraft.name}
                      onChange={(event) => setHomeTeamDraft((current) => ({ ...current, name: event.target.value }))}
                      placeholder="North Crew A"
                    />
                  </Field>
                  <Field label="Lead maid">
                    <select
                      value={homeTeamDraft.leadStaffId}
                      disabled={!homeTeamDraft.memberStaffIds.length}
                      onChange={(event) => setHomeTeamDraft((current) => ({ ...current, leadStaffId: event.target.value }))}
                    >
                      <option value="">{homeTeamDraft.memberStaffIds.length ? "Choose lead maid" : "Select maids first"}</option>
                      {maidStaff
                        .filter((maid) => homeTeamDraft.memberStaffIds.includes(maid.id))
                        .map((maid) => (
                          <option key={maid.id} value={maid.id}>
                            {getMaidLabel(maid)}
                          </option>
                        ))}
                    </select>
                  </Field>
                </div>
                <div>
                  <Field
                    label={`Maids (${homeTeamDraft.memberStaffIds.length} selected)`}
                    hint="Select the cleaners who normally work together."
                  >
                    <div className="scheduler-maid-picker">
                      {maidStaff.map((maid) => (
                        <label
                          key={maid.id}
                          className={`scheduler-maid-chip ${homeTeamDraft.memberStaffIds.includes(maid.id) ? "is-selected" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={homeTeamDraft.memberStaffIds.includes(maid.id)}
                            onChange={() =>
                              setHomeTeamDraft((current) => {
                                const memberStaffIds = toggleSelection(current.memberStaffIds, maid.id);
                                const nextLead =
                                  memberStaffIds.includes(current.leadStaffId) ? current.leadStaffId : memberStaffIds[0] ?? "";
                                return {
                                  ...current,
                                  memberStaffIds,
                                  leadStaffId: nextLead
                                };
                              })
                            }
                          />
                          <span>{getMaidLabel(maid)}</span>
                          {homeTeamDraft.leadStaffId === maid.id ? <small>Lead</small> : null}
                        </label>
                      ))}
                    </div>
                  </Field>
                </div>
                <div className="customer-directory-actions">
                  <Button disabled={saveHomeTeamMutation.isPending} onClick={submitHomeTeam}>
                    {homeTeamDraft.id ? "Save team" : "Create team"}
                  </Button>
                  {homeTeamDraft.id ? (
                    <Button variant="ghost" onClick={resetHomeTeamDraft}>
                      Cancel edit
                    </Button>
                  ) : null}
                </div>
              </div>
            </Card>
          </div>
          ) : null}

          {isDispatcher ? (
            <>
              <OpsMetricGrid>
                <OpsMetricCard
                  label="Visible bookings"
                  value={visibility?.visibleCount ?? board.bookings.length}
                  detail={`${selectedDate} at ${selectedLocationName}`}
                  icon={CalendarDays}
                  tone="blue"
                />
                <OpsMetricCard
                  label="Unassigned"
                  value={unassignedBookings.length}
                  detail={unassignedBookings.length ? "Needs team placement" : "All visible bookings assigned"}
                  icon={ClipboardList}
                  tone={unassignedBookings.length ? "amber" : "emerald"}
                />
                <OpsMetricCard
                  label="Ready teams"
                  value={readyTeamCount}
                  detail={`${board.teams.length} total day teams`}
                  icon={UserCheck}
                  tone={readyTeamCount ? "emerald" : "amber"}
                />
                <OpsMetricCard
                  label="Off staff"
                  value={board.offStaff.length}
                  detail="Marked off or unavailable"
                  icon={UserX}
                  tone={board.offStaff.length ? "amber" : "slate"}
                />
                <OpsMetricCard
                  label="Warnings"
                  value={warningCount}
                  detail={warningCount ? "Needs dispatcher attention" : "No board warnings"}
                  icon={AlertTriangle}
                  tone={warningCount ? "rose" : "emerald"}
                />
              </OpsMetricGrid>

              {!board.teams.length ? (
                <OpsPanel
                  eyebrow="Start here"
                  title="No day teams are built for this date yet"
                  actions={
                    <div className="customer-directory-actions">
                      <Button
                        disabled={!selectedLocationId || bootstrapMutation.isPending}
                        onClick={() => bootstrapMutation.mutate()}
                      >
                        Build teams from saved teams
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => document.getElementById("dispatcher-day-team-form")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                      >
                        Create day team manually
                      </Button>
                      <Link className="button button--secondary" to="/admin/settings/teams">
                        Open Teams
                      </Link>
                    </div>
                  }
                >
                  <p className="card-copy" style={{ margin: 0 }}>
                    Dispatcher does not create teams automatically. Build teams copies the saved cleaning teams for {selectedLocationName} into {selectedDate},
                    then you can adjust staffing and assign bookings.
                  </p>
                </OpsPanel>
              ) : null}

              <OpsPanel
                eyebrow="Bookings first"
                title={`Bookings for ${selectedDate}`}
                actions={
                  <Link className="button button--secondary" to={bookingsListUrl}>
                    Open filtered Bookings
                  </Link>
                }
              >
                {board.bookings.length ? (
                  <div className="booking-list">
                    {board.bookings.map((booking) => (
                      <DispatcherBookingOverviewCard
                        key={booking.bookingId}
                        booking={booking}
                        teams={board.teams}
                        onDragStart={() => setDraggingBookingId(booking.bookingId)}
                        onDragEnd={() => setDraggingBookingId("")}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="card-copy">
                    No bookings match this date, location, and active-status view. Use the explanation below to check what is hidden.
                  </p>
                )}
              </OpsPanel>

              <OpsPanel eyebrow="Visibility" title="Why don't I see bookings?">
                <div className="scheduler-visibility-panel">
                  <p>
                    Dispatcher shows bookings for <strong>{selectedDate}</strong>, <strong>{selectedLocationName}</strong>, and statuses{" "}
                    <strong>{(visibility?.includedStatuses ?? ["pending", "confirmed", "rescheduled"]).join(", ")}</strong>.
                  </p>
                  <div className="scheduler-visibility-counts">
                    <span>{visibility?.hiddenSameDate.otherLocationCount ?? 0} same-day bookings at other locations</span>
                    <span>{visibility?.hiddenSameDate.excludedStatusCount ?? 0} same-location bookings in excluded statuses</span>
                    <span>{visibility?.hiddenSameDate.missingLocationCount ?? 0} same-day bookings missing a location</span>
                  </div>
                  <Link className="button button--secondary" to={bookingsListUrl}>
                    View this date in Bookings
                  </Link>
                </div>
              </OpsPanel>

              {board.teams.length ? (
                <OpsPanel
                  eyebrow="Dispatch board"
                  title={
                    dispatcherView === "horizontal"
                      ? "Horizontal Timeline"
                      : dispatcherView === "vertical"
                        ? "Vertical Timeline"
                        : dispatcherView === "map"
                          ? "Map View"
                          : "Route List"
                  }
                  actions={<DispatcherViewSwitcher value={dispatcherView} onChange={setDispatcherView} />}
                >
                  <div className="dispatcher-team-schedule-grid">
                    {board.teams.map((team) => {
                      const plans = routePlansByTeamId.get(team.id) ?? [];
                      const firstPlan = plans[0];
                      const lastPlan = plans[plans.length - 1];
                      return (
                        <div className="dispatcher-team-schedule-card" key={team.id}>
                          <div>
                            <strong>{team.name}</strong>
                            <span>
                              {plans.length
                                ? `${formatMinutesAsClock(firstPlan.plannedStartMinutes)} - ${formatMinutesAsClock(lastPlan.plannedEndMinutes)} planned`
                                : "No stops yet"}
                            </span>
                          </div>
                          <label>
                            Team starts
                            <input
                              type="time"
                              value={routeStartDrafts[team.id] ?? ""}
                              onChange={(event) =>
                                setRouteStartDrafts((current) => ({
                                  ...current,
                                  [team.id]: event.target.value
                                }))
                              }
                            />
                          </label>
                          <div className="dispatcher-team-schedule-card__actions">
                            <Button
                              variant="secondary"
                              disabled={saveDayTeamMutation.isPending}
                              onClick={() => saveTeamRouteStart(team)}
                            >
                              Save start
                            </Button>
                            <Button
                              disabled={publishTeamRouteMutation.isPending || !plans.length}
                              onClick={() => publishTeamRouteMutation.mutate(team.id)}
                            >
                              {team.routePublishedAt ? "Republish" : "Publish"}
                            </Button>
                          </div>
                          {team.routePublishedAt ? <small>Published {team.routePublishedAt}</small> : null}
                        </div>
                      );
                    })}
                  </div>
                  {dispatcherRouteWarnings.length ? (
                    <div className="dispatcher-route-warning-list">
                      {dispatcherRouteWarnings.map((warning) => (
                        <div className={`dispatcher-route-warning dispatcher-route-warning--${warning.tone}`} key={warning.key}>
                          <AlertTriangle size={16} aria-hidden="true" />
                          <div>
                            <strong>{warning.teamName}</strong>
                            <span>{warning.message}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {dispatcherView === "horizontal" ? (
                    <DispatcherHorizontalTimelineView
                      board={board}
                      unassignedBookings={unassignedBookings}
                      routePlansByTeamId={routePlansByTeamId}
                      workday={dispatcherWorkday}
                      ticks={dispatcherTimelineTicks}
                      draggingBookingId={draggingBookingId}
                      setDraggingBookingId={setDraggingBookingId}
                      onAssignToTeam={assignDraggedBookingToTeam}
                      getMaidLabelById={getMaidLabelById}
                    />
                  ) : null}
                  {dispatcherView === "vertical" ? (
                    <DispatcherVerticalTimelineView
                      board={board}
                      unassignedBookings={unassignedBookings}
                      routePlansByTeamId={routePlansByTeamId}
                      workday={dispatcherWorkday}
                      ticks={dispatcherTimelineTicks}
                      draggingBookingId={draggingBookingId}
                      setDraggingBookingId={setDraggingBookingId}
                      onAssignToTeam={assignDraggedBookingToTeam}
                      getMaidLabelById={getMaidLabelById}
                    />
                  ) : null}
                  {dispatcherView === "map" ? (
                    <DispatcherMapView
                      board={board}
                      assignedBookingsByTeamId={assignedBookingsByTeamId}
                      unassignedBookings={unassignedBookings}
                      routePlansByTeamId={routePlansByTeamId}
                      draggingBookingId={draggingBookingId}
                      setDraggingBookingId={setDraggingBookingId}
                      onAssignToTeam={assignDraggedBookingToTeam}
                    />
                  ) : null}
                  {dispatcherView === "list" ? (
                    <DispatcherListView
                      board={board}
                      unassignedBookings={unassignedBookings}
                      routePlansByTeamId={routePlansByTeamId}
                      draggingBookingId={draggingBookingId}
                      setDraggingBookingId={setDraggingBookingId}
                      onAssignToTeam={assignDraggedBookingToTeam}
                      getMaidLabelById={getMaidLabelById}
                    />
                  ) : null}
                </OpsPanel>
              ) : null}
            </>
          ) : null}

          {isDispatcher ? (
          <>
          <Card title="Daily Overrides" eyebrow="Who is actually working">
            <div className="stack">
              <div className="scheduler-overrides-section">
                <strong>Available maids</strong>
                <div className="scheduler-staff-grid">
                  {(board.availableMaids ?? []).map((staff) => (
                    <div className="scheduler-staff-card" key={staff.staffId}>
                      <div className="scheduler-staff-card__top">
                        <strong>{staff.name}</strong>
                        <Badge tone={staff.source === "manual_override" ? "info" : "success"}>{staff.source}</Badge>
                      </div>
                      <div className="scheduler-staff-card__actions">
                        <Button
                          variant="secondary"
                          disabled={staffOverrideMutation.isPending}
                          onClick={() => saveOverrideState(staff.staffId, "off")}
                        >
                          Mark off
                        </Button>
                        {staff.source === "manual_override" ? (
                          <Button
                            variant="ghost"
                            disabled={staffOverrideMutation.isPending}
                            onClick={() => saveOverrideState(staff.staffId, "reset")}
                          >
                            Reset override
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="scheduler-overrides-section">
                <strong>Off staff</strong>
                <div className="scheduler-staff-grid">
                  {board.offStaff.map((staff) => (
                    <div className="scheduler-staff-card" key={staff.staffId}>
                      <div className="scheduler-staff-card__top">
                        <strong>{staff.name}</strong>
                        <Badge tone={buildToneForOffReason(staff.reason)}>{staff.reason}</Badge>
                      </div>
                      {staff.reason !== "time_off" ? (
                        <div className="scheduler-staff-card__actions">
                          <Button
                            variant="secondary"
                            disabled={staffOverrideMutation.isPending}
                            onClick={() =>
                              saveOverrideState(staff.staffId, staff.reason === "manual_off" ? "reset" : "available")
                            }
                          >
                            {staff.reason === "manual_off" ? "Reset override" : "Mark available"}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                  {!board.offStaff.length ? <p className="card-copy">No one is marked off for this day.</p> : null}
                </div>
              </div>
            </div>
          </Card>

          <div className="stack">
            <Card title="Day Teams" eyebrow="Date-specific staffing">
              <div className="booking-list">
                {board.teams.length ? (
                  board.teams.map((team) => (
                    <div className="booking-list-item" key={team.id} style={{ cursor: "default" }}>
                      <div className="booking-list-item__top">
                        <strong>{team.name}</strong>
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                          <Badge tone={team.teamState === "ready" ? "success" : "warning"}>{team.teamState}</Badge>
                          <Badge tone="info">{team.actualTeamSize}/{team.targetTeamSize}</Badge>
                        </div>
                      </div>
                      <p>{team.memberStaffIds.map((staffId, index) => getMaidLabelById(staffId, team.memberNames[index] ?? "")).join(" | ")}</p>
                      {team.notes ? <p>{team.notes}</p> : null}
                      <div className="customer-directory-actions" style={{ marginTop: "0.75rem" }}>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            setDayTeamDraft({
                              id: team.id,
                              name: team.name,
                              driverStaffId: "",
                              memberStaffIds: team.memberStaffIds,
                              targetTeamSize: String(team.targetTeamSize),
                              teamState: team.teamState,
                              routeStartTime: team.routeStartTime ?? "",
                              notes: team.notes
                            })
                          }
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          disabled={deleteDayTeamMutation.isPending}
                          onClick={() => {
                            if (window.confirm(`Delete ${team.name}?`)) {
                              deleteDayTeamMutation.mutate(team.id);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="card-copy">No day teams yet. Build the day from home teams or create one manually.</p>
                )}
              </div>
            </Card>

            <div id="dispatcher-day-team-form">
            <Card title={dayTeamDraft.id ? "Edit Day Team" : "New Day Team"} eyebrow="Ad hoc team for this date">
              <div className="scheduler-form-grid">
                <div className="scheduler-form-grid__full">
                  <Field label="Team name">
                    <input
                      value={dayTeamDraft.name}
                      onChange={(event) => setDayTeamDraft((current) => ({ ...current, name: event.target.value }))}
                      placeholder="South backup crew"
                    />
                  </Field>
                </div>
                <div>
                  <Field label="Target team size">
                    <input
                      type="number"
                      min={1}
                      value={dayTeamDraft.targetTeamSize}
                      onChange={(event) => setDayTeamDraft((current) => ({ ...current, targetTeamSize: event.target.value }))}
                    />
                  </Field>
                </div>
                <div>
                  <Field label="Team state">
                    <select
                      value={dayTeamDraft.teamState}
                      onChange={(event) =>
                        setDayTeamDraft((current) => ({
                          ...current,
                          teamState: event.target.value === "ready" ? "ready" : "needs_review"
                        }))
                      }
                    >
                      <option value="needs_review">Needs review</option>
                      <option value="ready">Ready</option>
                    </select>
                  </Field>
                </div>
                <div>
                  <Field label="Route start time">
                    <input
                      type="time"
                      value={dayTeamDraft.routeStartTime}
                      onChange={(event) => setDayTeamDraft((current) => ({ ...current, routeStartTime: event.target.value }))}
                    />
                  </Field>
                </div>
                <div className="scheduler-form-grid__full">
                  <Field label="Maids">
                    <div className="scheduler-choice-grid">
                      {availableMaidChoices.map((maid) => (
                        <label key={maid.staffId} className="scheduler-choice">
                          <input
                            type="checkbox"
                            checked={dayTeamDraft.memberStaffIds.includes(maid.staffId)}
                            onChange={() =>
                              setDayTeamDraft((current) => ({
                                ...current,
                                memberStaffIds: toggleSelection(current.memberStaffIds, maid.staffId)
                              }))
                            }
                          />
                          <span>{getMaidLabelById(maid.staffId, maid.name)}</span>
                        </label>
                      ))}
                    </div>
                  </Field>
                </div>
                <div className="scheduler-form-grid__full">
                  <Field label="Notes">
                    <textarea
                      rows={3}
                      value={dayTeamDraft.notes}
                      onChange={(event) => setDayTeamDraft((current) => ({ ...current, notes: event.target.value }))}
                    />
                  </Field>
                </div>
                <div className="customer-directory-actions scheduler-form-grid__full">
                  <Button disabled={saveDayTeamMutation.isPending} onClick={submitDayTeam}>
                    {dayTeamDraft.id ? "Save day team" : "Create day team"}
                  </Button>
                  {dayTeamDraft.id ? (
                    <Button variant="ghost" onClick={resetDayTeamDraft}>
                      Cancel edit
                    </Button>
                  ) : null}
                </div>
              </div>
            </Card>
            </div>
          </div>

          <div className="stack">
            <Card title="Board Summary" eyebrow="Daily snapshot">
              <div className="stack--tight">
                <p className="card-copy" style={{ marginBottom: 0 }}>
                  {board.bookings.length} active bookings
                </p>
                <p className="card-copy" style={{ marginBottom: 0 }}>
                  {board.teams.filter((team) => team.teamState === "ready").length} ready teams
                </p>
                <p className="card-copy" style={{ marginBottom: 0 }}>
                  {unassignedBookings.length} unassigned bookings
                </p>
                <p className="card-copy" style={{ marginBottom: 0 }}>
                  {board.offStaff.length} staff off or unavailable
                </p>
              </div>
            </Card>

            <Card title="Warnings" eyebrow="Needs attention">
              {board.day.warnings.length ? (
                <div className="customer-directory-actions">
                  {board.day.warnings.map((warning) => (
                    <Badge key={warning} tone={buildToneForWarning(warning)}>
                      {warning}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="card-copy">No board warnings right now.</p>
              )}
            </Card>

            <Card title="Sync Warnings" eyebrow="Legacy booking sync">
              {board.syncWarnings.length ? (
                <div className="stack--tight">
                  {board.syncWarnings.map((warning) => (
                    <p key={warning} className="card-copy" style={{ marginBottom: 0 }}>
                      {warning}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="card-copy">Scheduler and booking assignment sync is healthy.</p>
              )}
            </Card>
          </div>

          <div className="stack">
            <Card title={`Unassigned Bookings (${unassignedBookings.length})`} eyebrow="Needs placement">
              {!board.teams.length ? (
                <div className="scheduler-empty-action">
                  <Badge tone="warning">No day teams yet</Badge>
                  <p>
                    Build teams for {selectedDate} first. After day teams exist, this dropdown will show team names and you can assign the booking.
                  </p>
                  <div className="customer-directory-actions">
                    <Button
                      disabled={!selectedLocationId || bootstrapMutation.isPending}
                      onClick={() => bootstrapMutation.mutate()}
                    >
                      Build teams now
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => document.getElementById("dispatcher-day-team-form")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    >
                      Create day team manually
                    </Button>
                    <Link className="button button--secondary" to="/admin/settings/teams">
                      Edit saved teams
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="booking-list">
                  {unassignedBookings.length ? (
                    unassignedBookings.map((booking) => (
                      <SchedulerBookingAssignmentCard
                        key={booking.bookingId}
                        booking={booking}
                        teams={board.teams}
                        isPending={bookingAssignmentMutation.isPending && pendingBookingId === booking.bookingId}
                        onSave={(bookingId, payload) => bookingAssignmentMutation.mutate({ bookingId, payload })}
                        draggable
                        onDragStart={() => setDraggingBookingId(booking.bookingId)}
                        onDragEnd={() => setDraggingBookingId("")}
                      />
                    ))
                  ) : (
                    <p className="card-copy">Every active booking is assigned to a team.</p>
                  )}
                </div>
              )}
            </Card>

            <Card title="Assigned Booking Controls" eyebrow="Manual assignment fallback">
              <div className="booking-list">
                {board.teams.length ? (
                  board.teams.map((team) => {
                    const assignedBookings = assignedBookingsByTeamId.get(team.id) ?? [];
                    return (
                      <div
                        className={`booking-list-item scheduler-team-lane ${draggingBookingId ? "is-drop-ready" : ""}`}
                        key={team.id}
                        onDragOver={(event) => {
                          if (draggingBookingId) {
                            event.preventDefault();
                          }
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          const bookingId = event.dataTransfer.getData("text/plain") || draggingBookingId;
                          if (bookingId) {
                            assignDraggedBookingToTeam(team.id, bookingId);
                          }
                        }}
                        style={{ cursor: "default" }}
                      >
                        <div className="booking-list-item__top">
                          <div>
                            <strong>{team.name}</strong>
                            <p>{team.memberStaffIds.map((staffId, index) => getMaidLabelById(staffId, team.memberNames[index] ?? "")).join(" | ")}</p>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <Badge tone={team.teamState === "ready" ? "success" : "warning"}>{team.teamState}</Badge>
                            <p>{assignedBookings.length} bookings</p>
                          </div>
                        </div>
                        <div className="booking-list-item__meta">
                          <span>Cleaning team</span>
                          <span>{team.actualTeamSize}/{team.targetTeamSize} maids</span>
                        </div>
                        {assignedBookings.length ? (
                          <div className="booking-list" style={{ marginTop: "0.85rem" }}>
                            {assignedBookings.map((booking) => (
                              <SchedulerBookingAssignmentCard
                                key={booking.bookingId}
                                booking={booking}
                                teams={board.teams}
                                isPending={bookingAssignmentMutation.isPending && pendingBookingId === booking.bookingId}
                                onSave={(bookingId, payload) => bookingAssignmentMutation.mutate({ bookingId, payload })}
                                draggable
                                onDragStart={() => setDraggingBookingId(booking.bookingId)}
                                onDragEnd={() => setDraggingBookingId("")}
                              />
                            ))}
                          </div>
                        ) : (
                          <p className="card-copy" style={{ marginTop: "0.75rem", marginBottom: 0 }}>
                            No bookings on this team yet.
                          </p>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="card-copy">No day teams are available yet.</p>
                )}
              </div>
            </Card>
          </div>
          </>
          ) : null}
        </>
      ) : null}
    </OpsPageShell>
  );
}
